/** tm/legal/alertUtil.ts — 새 소식 항목 정리: 중요도 순 정렬(법령 개정 > 국회 법안 > 국무회의 의결 > 입법예고 > 판례·해석) · 한 줄 미끼 문구 · 이동 파라미터 */
import type { AlertItem, AlertKey, ItemKind, LegalAlerts, LegalNavParams } from './legalTypes';

export interface AlertEntry { it: AlertItem; key: AlertKey }

const RANK: Record<AlertKey, number> = { law: 0, bill: 1, cabinet: 2, notice: 3, legal: 4 };
const ts = (s: string | null | undefined) => { const t = Date.parse(s || ''); return Number.isNaN(t) ? 0 : t; };

/** 모든 그룹 항목을 펼친다 (after 가 있으면 그보다 늦게 들어온 것만) */
export function entries(d: LegalAlerts, after?: string | null): AlertEntry[] {
  const min = ts(after);
  const out: AlertEntry[] = [];
  for (const g of d.groups || []) for (const it of g.items || []) if (!min || ts(it.first_seen_at) > min) out.push({ it, key: g.key });
  return out;
}

/** 중요도 → 최신 순. 서버가 고른 top(점수 순)이 있으면 그 순서를 먼저 따른다 */
export function ranked(list: AlertEntry[], d?: LegalAlerts): AlertEntry[] {
  const pos = new Map((d?.top || []).map((t, i) => [`${t.group}${t.id}`, i]));
  const at = (e: AlertEntry) => pos.get(`${e.key}${e.it.id}`) ?? 99;
  return [...list].sort((a, b) => at(a) - at(b) || (RANK[a.key] ?? 9) - (RANK[b.key] ?? 9)
    || Number(!!a.it.minor) - Number(!!b.it.minor) || ts(b.it.first_seen_at) - ts(a.it.first_seen_at));
}

/** 응답의 latest_first_seen_at 과 항목들 중 가장 늦은 값 */
export function latestOf(d: LegalAlerts): string | null {
  let best: string | null = d.latest_first_seen_at || null;
  for (const e of entries(d)) if (!best || ts(e.it.first_seen_at) > ts(best)) best = e.it.first_seen_at;
  return best;
}

/** 그룹 → 항목 종류 */
export const kindOf = (key: AlertKey): ItemKind => (key === 'bill' ? 'bill' : key === 'notice' ? 'notice' : key === 'cabinet' ? 'cabinet' : 'legal');
export const navOf = (e: AlertEntry): LegalNavParams => ({ topic: e.it.topic || undefined, itemId: e.it.id, kind: kindOf(e.key) });

/** 배지 (그룹 색 이름 + 짧은 이름) */
export function badgeOf(e: AlertEntry): { group: string; label: string } {
  if (e.key === 'law') return { group: '법령', label: e.it.kind_label === '제정' ? '법령 신설' : '법령 개정' };
  if (e.key === 'bill') return { group: '국회 법안', label: '국회 법안' };
  if (e.key === 'notice') return { group: '입법예고', label: '입법예고' };
  if (e.key === 'cabinet') return { group: '국무회의', label: '국무회의 의결' };
  const s = e.it.source_label || '';
  return { group: /판례|심판|결정/.test(s) ? (/심판|결정/.test(s) ? '심판례' : '판례') : '유권해석', label: s.replace(/ 결정$/, '') || '판례·해석' };
}

/** 미끼 문구 색 — 앞으로 있을 일(시행 D-n·의견제출 D-n)만 파랑, 이미 시행 중·그 밖은 회색 */
export const hookColor = (e: AlertEntry, blue: string, gray: string) => (/D-\d|오늘 (시행|마감)/.test(hookOf(e)) ? blue : gray);

/** 한 줄 미끼 문구 — 서버 hook 우선, 없으면 종류별 기본 문구 */
export function hookOf(e: AlertEntry): string {
  const h = (e.it.hook || '').trim();
  if (h) return h;
  if (e.key === 'law') return `${e.it.kind_label || '개정'} 공포${e.it.minor ? ' (타법개정)' : ''}`;
  if (e.key === 'bill') return e.it.kind_label ? `상태: ${e.it.kind_label}` : '새 법률안 발의';
  if (e.key === 'notice') return '의견제출 기간 중인 입법예고';
  if (e.key === 'cabinet') return '국무회의 의결';
  return e.it.source_label || '새 판례·해석';
}

/** 그룹 합계 (count 가 없으면 항목 수) */
export const groupCount = (d: LegalAlerts, key: AlertKey) => {
  const g = d.groups.find(x => x.key === key);
  return g ? g.count ?? g.items.length : 0;
};
